import type { BoardState, HarmonyReport, Node, Pathway, StrikeResult } from '@/types';
import { FREQUENCY_COLORS, FREQUENCY_GLOW_COLORS, FREQUENCY_NAMES, PathwayType } from '@/types';
import type { ColorBlindMode } from '@/accessibility/colorblind';
import { getFrequencyColors } from '@/accessibility/colorblind';
import { ParticleSystem } from './particles';

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;
  private boardPadding = 60;
  private nodeRadius = 20;

  // Animation state
  private animating = false;
  private animationFrameId: number | null = null;
  private animationStartTime = 0;
  private preStrikeBoard: BoardState | null = null;
  private strikeResult: StrikeResult | null = null;
  private postStrikeReport: HarmonyReport | null = null;
  private animationCallback: (() => void) | null = null;

  // Celebration state
  private celebrating = false;
  private celebrationStart = 0;
  private celebrationBoard: BoardState | null = null;
  private celebrationReport: HarmonyReport | null = null;
  private celebrationCallback: (() => void) | null = null;

  // Accessibility state
  private colorBlindMode: ColorBlindMode = 'none';
  private reducedMotion = false;
  private showFrequencyNumbers = true;

  // Starfield cache for stable rendering
  private starfield: { x: number; y: number; size: number; opacity: number }[] | null = null;
  private starfieldWidth = 0;
  private starfieldHeight = 0;

  // Particle system
  private particles: ParticleSystem;
  private lastRenderTime = 0;

  // Keyboard focus indicator
  private _focusedNodeIndex = -1;

  // Hint display state
  private _hintHighlightNodeId: string | null = null;
  private _hintSequence: string[] = [];

  constructor(ctx: CanvasRenderingContext2D, width: number, height: number) {
    this.ctx = ctx;
    this.width = width;
    this.height = height;
    this.particles = new ParticleSystem();
  }

  set focusedNodeIndex(index: number) {
    this._focusedNodeIndex = index;
  }

  get focusedNodeIndex(): number {
    return this._focusedNodeIndex;
  }

  set hintHighlightNodeId(id: string | null) {
    this._hintHighlightNodeId = id;
  }

  get hintHighlightNodeId(): string | null {
    return this._hintHighlightNodeId;
  }

  set hintSequence(seq: string[]) {
    this._hintSequence = seq;
  }

  get hintSequence(): string[] {
    return this._hintSequence;
  }

  setColorBlindMode(mode: ColorBlindMode): void {
    this.colorBlindMode = mode;
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
  }

  setShowFrequencyNumbers(show: boolean): void {
    this.showFrequencyNumbers = show;
  }

  /** Returns the appropriate color for a frequency based on color blind mode. */
  private getNodeColor(frequency: number): string {
    if (this.colorBlindMode !== 'none') {
      const palette = getFrequencyColors(this.colorBlindMode);
      return palette[frequency] ?? '#888';
    }
    return FREQUENCY_COLORS[frequency] ?? '#888';
  }

  /** Returns the glow color for a frequency, with color blind fallback. */
  private getGlowColor(frequency: number): string {
    if (this.colorBlindMode !== 'none') {
      // For color blind modes, lighten the node color for glow
      const nodeColor = this.getNodeColor(frequency);
      return this.lightenColor(nodeColor, 40);
    }
    return FREQUENCY_GLOW_COLORS[frequency] ?? '#aaa';
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  render(board: BoardState, harmonyReport: HarmonyReport | null): void {
    // Delta time calculation for particles
    const now = performance.now();
    const deltaTime = this.lastRenderTime > 0 ? (now - this.lastRenderTime) / 1000 : 0.016;
    this.lastRenderTime = now;

    this.drawBackground();

    // Build a lookup of pathway statuses by pathway id
    const pathwayStatusMap = new Map<string, { satisfied: boolean }>();
    if (harmonyReport) {
      for (const ps of harmonyReport.pathways) {
        pathwayStatusMap.set(ps.pathwayId, { satisfied: ps.satisfied });
      }
    }

    // Draw pathways first (behind nodes)
    for (const pathway of board.pathways) {
      const sourceNode = board.nodes.find((n) => n.id === pathway.source);
      const targetNode = board.nodes.find((n) => n.id === pathway.target);
      if (!sourceNode || !targetNode) continue;

      const status = pathwayStatusMap.get(pathway.id);
      this.drawPathway(pathway, sourceNode, targetNode, status);
    }

    // Update and render particles (between pathways and nodes)
    if (!this.reducedMotion) {
      this.particles.update(deltaTime);
      this.particles.render(this.ctx);
    }

    // Draw nodes on top with idle pulse
    const time = now / 1000;
    for (let i = 0; i < board.nodes.length; i++) {
      const node = board.nodes[i];
      if (!node) continue;
      const isFocused = i === this._focusedNodeIndex;
      this.drawNode(node, time, isFocused);
    }
  }

  /**
   * Animate a strike result with ring-timed transitions.
   *
   * Timeline:
   *   0-150ms:   Flash the struck node (bright white flash fading out)
   *   200-500ms: Ring 1 nodes transition color (old -> new)
   *   400-700ms: Ring 2 nodes transition color
   *   600-900ms: Ring 3 nodes transition color
   *   After 900ms: call onComplete
   */
  animateStrike(
    preBoard: BoardState,
    strikeResult: StrikeResult,
    harmonyReport: HarmonyReport | null,
    onComplete: () => void,
  ): void {
    // Cancel any running animation
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // If reduced motion is enabled, skip animation and apply final state immediately
    if (this.reducedMotion) {
      this.render(strikeResult.newBoard, harmonyReport);
      onComplete();
      return;
    }

    this.animating = true;
    this.preStrikeBoard = preBoard;
    this.strikeResult = strikeResult;
    this.postStrikeReport = harmonyReport;
    this.animationCallback = onComplete;
    this.animationStartTime = performance.now();
    this.lastRenderTime = this.animationStartTime;

    // Emit burst particles from the struck node
    if (!this.reducedMotion) {
      const struckNode = preBoard.nodes.find(n => n.id === strikeResult.struckNodeId);
      if (struckNode) {
        const sx = this.toCanvasX(struckNode.x);
        const sy = this.toCanvasY(struckNode.y);
        const glowColor = this.getGlowColor(struckNode.frequency);
        this.particles.emitBurst(sx, sy, 30, glowColor, 80, 0.8);
      }
    }

    this.animationTick(this.animationStartTime);
  }

  /** Returns true if an animation or celebration is currently running. */
  isAnimating(): boolean {
    return this.animating || this.celebrating;
  }

  /**
   * Plays a 2.5s celebration animation: pathways flash golden-white,
   * nodes pulse in unison, background warms up.
   */
  playCelebration(board: BoardState, harmonyReport: HarmonyReport | null, onComplete?: () => void): void {
    if (this.reducedMotion) {
      // Just render final state with a brief golden tint
      this.render(board, harmonyReport);
      onComplete?.();
      return;
    }

    this.celebrating = true;
    this.celebrationStart = performance.now();
    this.celebrationBoard = board;
    this.celebrationReport = harmonyReport;
    this.celebrationCallback = onComplete ?? null;

    const duration = 2500;

    const tick = () => {
      const elapsed = performance.now() - this.celebrationStart;
      const t = Math.min(elapsed / duration, 1);

      this.renderCelebrationFrame(t);

      if (t < 1) {
        this.animationFrameId = requestAnimationFrame(tick);
      } else {
        this.celebrating = false;
        this.celebrationBoard = null;
        this.celebrationReport = null;
        this.render(board, harmonyReport);
        this.celebrationCallback?.();
        this.celebrationCallback = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(tick);
  }

  private renderCelebrationFrame(t: number): void {
    const board = this.celebrationBoard;
    const report = this.celebrationReport;
    if (!board) return;

    const ctx = this.ctx;

    // Delta time for particles
    const now = performance.now();
    const deltaTime = this.lastRenderTime > 0 ? (now - this.lastRenderTime) / 1000 : 0.016;
    this.lastRenderTime = now;

    // Emit celebration particles (spiral from center, ~10 per frame for first 1.5s)
    if (!this.reducedMotion && t < 0.6) { // 0.6 * 2500ms = 1500ms
      const centerX = this.width / 2;
      const centerY = this.height / 2;
      const angle = now * 0.003; // slow spiral
      const spread = 60;
      const bx = centerX + Math.cos(angle) * spread;
      const by = centerY + Math.sin(angle) * spread;
      this.particles.emitBurst(bx, by, 10, '#FCBF49', 50, 1.5);
    }

    // Draw cosmic background (warmth modulation happens via overlay)
    this.drawBackground();

    // Warm overlay that peaks at midpoint
    const warmth = Math.sin(t * Math.PI) * 0.15;
    ctx.fillStyle = `rgba(${Math.round(warmth * 180)}, ${Math.round(warmth * 120)}, ${Math.round(warmth * 40)}, ${warmth})`;
    ctx.fillRect(0, 0, this.width, this.height);

    // Draw pathways with golden flash
    const flashIntensity = t < 0.3 ? t / 0.3 : t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
    const pathwayStatusMap = new Map<string, { satisfied: boolean }>();
    if (report) {
      for (const ps of report.pathways) {
        pathwayStatusMap.set(ps.pathwayId, { satisfied: ps.satisfied });
      }
    }

    // All pathways glow golden
    for (const pathway of board.pathways) {
      const sourceNode = board.nodes.find((n) => n.id === pathway.source);
      const targetNode = board.nodes.find((n) => n.id === pathway.target);
      if (!sourceNode || !targetNode) continue;

      const sx = this.toCanvasX(sourceNode.x);
      const sy = this.toCanvasY(sourceNode.y);
      const tx = this.toCanvasX(targetNode.x);
      const ty = this.toCanvasY(targetNode.y);

      ctx.save();
      ctx.strokeStyle = `rgba(252, 191, 73, ${0.3 + flashIntensity * 0.6})`;
      ctx.lineWidth = 2 + flashIntensity * 2;
      ctx.shadowColor = 'rgba(252, 191, 73, 0.6)';
      ctx.shadowBlur = 12 * flashIntensity;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      ctx.restore();
    }

    // Update and render particles (between pathways and nodes)
    if (!this.reducedMotion) {
      this.particles.update(deltaTime);
      this.particles.render(ctx);
    }

    // Draw nodes with synchronized pulse
    const pulsePhase = Math.sin(t * Math.PI * 4) * 0.3 + 1; // 4 pulse cycles
    for (const node of board.nodes) {
      const cx = this.toCanvasX(node.x);
      const cy = this.toCanvasY(node.y);
      const r = this.nodeRadius * pulsePhase;
      const nodeColor = this.getNodeColor(node.frequency);
      const glowColor = this.getGlowColor(node.frequency);

      ctx.save();

      // Golden celebration glow halo (additive)
      ctx.globalCompositeOperation = 'lighter';
      const celebGlowRadius = r * 2.5 + flashIntensity * 15;
      const celebGlow = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, celebGlowRadius);
      celebGlow.addColorStop(0, `rgba(252, 191, 73, ${flashIntensity * 0.35})`);
      celebGlow.addColorStop(0.5, `rgba(252, 191, 73, ${flashIntensity * 0.15})`);
      celebGlow.addColorStop(1, 'rgba(252, 191, 73, 0)');
      ctx.fillStyle = celebGlow;
      ctx.fillRect(cx - celebGlowRadius, cy - celebGlowRadius, celebGlowRadius * 2, celebGlowRadius * 2);

      // Node glow halo (in node color)
      const haloRadius = r * 2;
      const haloGrad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, haloRadius);
      haloGrad.addColorStop(0, this.colorWithOpacity(glowColor, 0.3));
      haloGrad.addColorStop(1, this.colorWithOpacity(glowColor, 0));
      ctx.fillStyle = haloGrad;
      ctx.fillRect(cx - haloRadius, cy - haloRadius, haloRadius * 2, haloRadius * 2);

      ctx.globalCompositeOperation = 'source-over';

      // Core orb gradient
      const coreGrad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
      coreGrad.addColorStop(0, this.lightenColor(nodeColor, 40));
      coreGrad.addColorStop(0.7, nodeColor);
      coreGrad.addColorStop(1, this.darkenColor(nodeColor, 30));

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();

      // Golden border during celebration
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = `rgba(252, 191, 73, ${0.5 + flashIntensity * 0.5})`;
      ctx.stroke();

      // Specular highlight
      ctx.beginPath();
      ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.12 + flashIntensity * 0.08})`;
      ctx.fill();

      // Frequency label
      if (this.showFrequencyNumbers) {
        const name = FREQUENCY_NAMES[node.frequency] ?? '?';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 3;
        ctx.fillText(name, cx, cy);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      ctx.restore();
    }
  }

  hitTest(canvasX: number, canvasY: number, board: BoardState): string | null {
    // Check nodes in reverse order so topmost (last drawn) is tested first
    for (let i = board.nodes.length - 1; i >= 0; i--) {
      const node = board.nodes[i];
      if (!node) continue;
      const cx = this.toCanvasX(node.x);
      const cy = this.toCanvasY(node.y);
      const dx = canvasX - cx;
      const dy = canvasY - cy;
      if (dx * dx + dy * dy <= this.nodeRadius * this.nodeRadius) {
        return node.id;
      }
    }
    return null;
  }

  // ── Animation internals ──

  private animationTick = (now: number): void => {
    if (!this.animating || !this.preStrikeBoard || !this.strikeResult) return;

    const elapsed = now - this.animationStartTime;
    const totalDuration = 950; // slightly past 900ms to ensure final frame renders

    // Build the interpolated board for this frame
    this.renderAnimationFrame(elapsed);

    if (elapsed >= totalDuration) {
      // Animation complete
      this.animating = false;
      this.animationFrameId = null;

      // Render final state
      this.render(this.strikeResult.newBoard, this.postStrikeReport);

      const callback = this.animationCallback;
      this.preStrikeBoard = null;
      this.strikeResult = null;
      this.postStrikeReport = null;
      this.animationCallback = null;

      if (callback) callback();
      return;
    }

    this.animationFrameId = requestAnimationFrame(this.animationTick);
  };

  private renderAnimationFrame(elapsed: number): void {
    if (!this.preStrikeBoard || !this.strikeResult) return;

    const preBoard = this.preStrikeBoard;
    const result = this.strikeResult;
    const struckNodeId = result.struckNodeId;

    // Delta time for particles
    const now = performance.now();
    const deltaTime = this.lastRenderTime > 0 ? (now - this.lastRenderTime) / 1000 : 0.016;
    this.lastRenderTime = now;

    // Build a transmutation map: nodeId -> { oldFreq, newFreq, ring }
    const transMap = new Map<
      string,
      { oldFrequency: number; newFrequency: number; ring: number }
    >();
    for (const t of result.transmutations) {
      transMap.set(t.nodeId, {
        oldFrequency: t.oldFrequency,
        newFrequency: t.newFrequency,
        ring: t.ring,
      });
    }

    // Clear and draw background
    this.drawBackground();

    // For pathways, use pre-strike state during animation
    for (const pathway of preBoard.pathways) {
      const sourceNode = preBoard.nodes.find((n) => n.id === pathway.source);
      const targetNode = preBoard.nodes.find((n) => n.id === pathway.target);
      if (!sourceNode || !targetNode) continue;
      this.drawPathway(pathway, sourceNode, targetNode, undefined);
    }

    // Update and render particles
    if (!this.reducedMotion) {
      this.particles.update(deltaTime);
      this.particles.render(this.ctx);
    }

    // Draw nodes with animation effects
    for (const node of preBoard.nodes) {
      if (node.id === struckNodeId) {
        // Flash effect for struck node: 0-150ms
        this.drawNode(node);
        if (elapsed <= 150) {
          const flashT = 1.0 - elapsed / 150; // 1.0 -> 0.0
          this.drawNodeFlash(node, flashT);
        }
      } else {
        const trans = transMap.get(node.id);
        if (trans) {
          // Determine transition progress based on ring
          const t = this.getRingTransitionProgress(trans.ring, elapsed);
          if (t <= 0) {
            // Not started yet - draw with old frequency
            this.drawNode(node);
          } else if (t >= 1) {
            // Fully transitioned - draw with new frequency
            this.drawNodeWithFrequency(node, trans.newFrequency);
          } else {
            // Interpolate color
            this.drawNodeInterpolated(node, trans.oldFrequency, trans.newFrequency, t);
          }
        } else {
          // No transmutation - draw normally
          this.drawNode(node);
        }
      }
    }
  }

  /**
   * Returns a 0-1 progress for a ring's color transition.
   * Ring 1: 200-500ms
   * Ring 2: 400-700ms
   * Ring 3: 600-900ms
   */
  private getRingTransitionProgress(ring: number, elapsed: number): number {
    const ringStart = ring === 1 ? 200 : ring === 2 ? 400 : 600;
    const ringEnd = ringStart + 300;

    if (elapsed < ringStart) return 0;
    if (elapsed >= ringEnd) return 1;

    // Ease in-out
    const raw = (elapsed - ringStart) / (ringEnd - ringStart);
    return this.easeInOut(raw);
  }

  private easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  private drawNodeFlash(node: Node, intensity: number): void {
    const ctx = this.ctx;
    const cx = this.toCanvasX(node.x);
    const cy = this.toCanvasY(node.y);
    const r = this.nodeRadius + 4;
    const glowColor = this.getGlowColor(node.frequency);

    ctx.save();

    // Additive blending for energy burst effect
    ctx.globalCompositeOperation = 'lighter';

    // Outer energy burst
    const burstRadius = r + 16 * intensity;
    const burstGrad = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, burstRadius);
    burstGrad.addColorStop(0, `rgba(255, 255, 255, ${0.5 * intensity})`);
    burstGrad.addColorStop(0.4, this.colorWithOpacity(glowColor, 0.4 * intensity));
    burstGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = burstGrad;
    ctx.fillRect(cx - burstRadius, cy - burstRadius, burstRadius * 2, burstRadius * 2);

    // Bright core flash
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${0.6 * intensity})`;
    ctx.fill();

    ctx.restore();
  }

  private drawNodeWithFrequency(node: Node, frequency: number): void {
    const ctx = this.ctx;
    const cx = this.toCanvasX(node.x);
    const cy = this.toCanvasY(node.y);
    const r = this.nodeRadius;

    ctx.save();

    const fillColor = this.getNodeColor(frequency);
    const glowColor = this.getGlowColor(frequency);

    // Outer glow halo (additive blending)
    ctx.globalCompositeOperation = 'lighter';
    const haloRadius = r * 2;
    const haloGrad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, haloRadius);
    haloGrad.addColorStop(0, this.colorWithOpacity(glowColor, 0.25));
    haloGrad.addColorStop(0.6, this.colorWithOpacity(glowColor, 0.08));
    haloGrad.addColorStop(1, this.colorWithOpacity(glowColor, 0));
    ctx.fillStyle = haloGrad;
    ctx.fillRect(cx - haloRadius, cy - haloRadius, haloRadius * 2, haloRadius * 2);

    ctx.globalCompositeOperation = 'source-over';

    // Core orb with radial gradient (glass/gem look)
    const coreGrad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
    coreGrad.addColorStop(0, this.lightenColor(fillColor, 40));
    coreGrad.addColorStop(0.7, fillColor);
    coreGrad.addColorStop(1, this.darkenColor(fillColor, 30));

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = coreGrad;
    ctx.fill();

    // Subtle border in lighter shade of node color
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = node.locked
      ? this.darkenColor(fillColor, 40)
      : this.colorWithOpacity(this.lightenColor(fillColor, 30), 0.6);
    ctx.stroke();

    // Specular highlight
    ctx.beginPath();
    ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fill();

    if (node.locked) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fill();
      this.drawCrosshatch(cx, cy, r);
    }

    if (this.showFrequencyNumbers) {
      const name = FREQUENCY_NAMES[frequency] ?? '?';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 3;
      ctx.fillText(name, cx, cy);
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    ctx.restore();
  }

  private drawNodeInterpolated(
    node: Node,
    oldFreq: number,
    newFreq: number,
    t: number,
  ): void {
    const ctx = this.ctx;
    const cx = this.toCanvasX(node.x);
    const cy = this.toCanvasY(node.y);
    const r = this.nodeRadius;

    const oldColor = this.getNodeColor(oldFreq);
    const newColor = this.getNodeColor(newFreq);
    const interpColor = this.lerpColor(oldColor, newColor, t);

    const oldGlow = this.getGlowColor(oldFreq);
    const newGlow = this.getGlowColor(newFreq);
    const interpGlow = this.lerpColor(oldGlow, newGlow, t);

    ctx.save();

    // Outer glow halo (additive blending)
    ctx.globalCompositeOperation = 'lighter';
    const haloRadius = r * 2;
    const haloGrad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, haloRadius);
    haloGrad.addColorStop(0, this.colorWithOpacity(interpGlow, 0.25));
    haloGrad.addColorStop(0.6, this.colorWithOpacity(interpGlow, 0.08));
    haloGrad.addColorStop(1, this.colorWithOpacity(interpGlow, 0));
    ctx.fillStyle = haloGrad;
    ctx.fillRect(cx - haloRadius, cy - haloRadius, haloRadius * 2, haloRadius * 2);

    ctx.globalCompositeOperation = 'source-over';

    // Core orb gradient
    const coreGrad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
    coreGrad.addColorStop(0, this.lightenColor(interpColor, 40));
    coreGrad.addColorStop(0.7, interpColor);
    coreGrad.addColorStop(1, this.darkenColor(interpColor, 30));

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = coreGrad;
    ctx.fill();

    // Subtle border
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = node.locked
      ? this.darkenColor(interpColor, 40)
      : this.colorWithOpacity(this.lightenColor(interpColor, 30), 0.6);
    ctx.stroke();

    // Specular highlight
    ctx.beginPath();
    ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fill();

    if (node.locked) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fill();
      this.drawCrosshatch(cx, cy, r);
    }

    if (this.showFrequencyNumbers) {
      // Show the target frequency name once past 50% transition
      const freq = t >= 0.5 ? newFreq : oldFreq;
      const name = FREQUENCY_NAMES[freq] ?? '?';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 3;
      ctx.fillText(name, cx, cy);
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    ctx.restore();
  }

  /**
   * Linearly interpolate between two hex colors.
   */
  private lerpColor(colorA: string, colorB: string, t: number): string {
    const [rA, gA, bA] = this.parseColor(colorA);
    const [rB, gB, bB] = this.parseColor(colorB);

    const r = Math.round(rA + (rB - rA) * t);
    const g = Math.round(gA + (gB - gA) * t);
    const b = Math.round(bA + (bB - bA) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }

  // ── Color helpers ──

  private parseHex(hex: string): [number, number, number] {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  }

  /** Parse hex or rgb() string into [r,g,b]. */
  private parseColor(color: string): [number, number, number] {
    if (color.startsWith('rgb(')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match && match[1] && match[2] && match[3]) {
        return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
      }
    }
    return this.parseHex(color);
  }

  private lightenColor(color: string, percent: number): string {
    const [r, g, b] = this.parseColor(color);
    const factor = percent / 100;
    const nr = Math.min(255, Math.round(r + (255 - r) * factor));
    const ng = Math.min(255, Math.round(g + (255 - g) * factor));
    const nb = Math.min(255, Math.round(b + (255 - b) * factor));
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
  }

  private darkenColor(color: string, percent: number): string {
    const [r, g, b] = this.parseColor(color);
    const factor = 1 - percent / 100;
    const nr = Math.max(0, Math.round(r * factor));
    const ng = Math.max(0, Math.round(g * factor));
    const nb = Math.max(0, Math.round(b * factor));
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
  }

  // ── Private helpers ──

  private toCanvasX(normalizedX: number): number {
    return this.boardPadding + normalizedX * (this.width - 2 * this.boardPadding);
  }

  private toCanvasY(normalizedY: number): number {
    return this.boardPadding + normalizedY * (this.height - 2 * this.boardPadding);
  }

  private drawBackground(): void {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    // Base: deep cosmic gradient (radial, dark blue center fading darker at edges)
    const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.7);
    bg.addColorStop(0, '#0d0d2b');   // slightly lighter center
    bg.addColorStop(0.5, '#0a0a1e'); // mid
    bg.addColorStop(1, '#050510');   // near-black edges
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Subtle starfield (cached for stable rendering)
    this.drawStarfield();
  }

  /** Generates and draws a deterministic starfield, caching positions on first call. */
  private drawStarfield(): void {
    // Regenerate starfield if dimensions changed
    if (!this.starfield || this.starfieldWidth !== this.width || this.starfieldHeight !== this.height) {
      this.starfieldWidth = this.width;
      this.starfieldHeight = this.height;
      this.starfield = [];

      // Seeded pseudo-random number generator (simple LCG)
      let seed = 42;
      const rand = (): number => {
        seed = (seed * 1664525 + 1013904223) & 0x7fffffff;
        return seed / 0x7fffffff;
      };

      // ~100 standard stars
      for (let i = 0; i < 100; i++) {
        this.starfield.push({
          x: rand() * this.width,
          y: rand() * this.height,
          size: 0.5 + rand() * 1.0, // 0.5-1.5px
          opacity: 0.1 + rand() * 0.35, // 0.1-0.45
        });
      }

      // ~8 brighter accent stars
      for (let i = 0; i < 8; i++) {
        this.starfield.push({
          x: rand() * this.width,
          y: rand() * this.height,
          size: 1.0 + rand() * 0.5, // 1.0-1.5px
          opacity: 0.55 + rand() * 0.15, // 0.55-0.7
        });
      }
    }

    const ctx = this.ctx;
    for (const star of this.starfield) {
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(220, 225, 255, ${star.opacity})`;
      ctx.fill();
    }
  }

  /**
   * Draws a crosshatch pattern (X pattern) on a locked node.
   * 3 thin diagonal lines in each direction across the node.
   */
  private drawCrosshatch(cx: number, cy: number, r: number): void {
    const ctx = this.ctx;
    ctx.save();

    // Clip to the circle so lines don't extend beyond the node
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();

    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1;

    // Draw 3 diagonal lines in the \ direction
    const offsets = [-r * 0.5, 0, r * 0.5];
    for (const offset of offsets) {
      ctx.beginPath();
      ctx.moveTo(cx - r + offset, cy - r);
      ctx.lineTo(cx + r + offset, cy + r);
      ctx.stroke();
    }

    // Draw 3 diagonal lines in the / direction
    for (const offset of offsets) {
      ctx.beginPath();
      ctx.moveTo(cx + r + offset, cy - r);
      ctx.lineTo(cx - r + offset, cy + r);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawPathway(
    pathway: Pathway,
    sourceNode: Node,
    targetNode: Node,
    status?: { satisfied: boolean },
  ): void {
    const ctx = this.ctx;
    const sx = this.toCanvasX(sourceNode.x);
    const sy = this.toCanvasY(sourceNode.y);
    const tx = this.toCanvasX(targetNode.x);
    const ty = this.toCanvasY(targetNode.y);

    ctx.save();

    // Create gradient from source glow color to target glow color
    const sourceColor = this.getGlowColor(sourceNode.frequency);
    const targetColor = this.getGlowColor(targetNode.frequency);
    const satisfied = status ? status.satisfied : true;

    switch (pathway.type) {
      case PathwayType.Amplifying:
        this.drawAmplifyingPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
      case PathwayType.Dampening:
        this.drawDampeningPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
      case PathwayType.Inverting:
        this.drawInvertingPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
      case PathwayType.Filtering:
        this.drawFilteringPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
      case PathwayType.Resonant:
        this.drawResonantPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
      case PathwayType.Clear:
      default:
        this.drawClearPathway(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
        break;
    }

    // Draw arrowhead for directed pathways
    if (pathway.directed) {
      this.drawArrowhead(ctx, sx, sy, tx, ty, sourceColor, targetColor, satisfied);
    }

    // Reset dash and shadow
    ctx.setLineDash([]);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Draw harmonic requirement label at midpoint (smaller, less prominent)
    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;
    // Offset label slightly away from the pathway
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    const offsetX = len > 0 ? (-dy / len) * 10 : 0;
    const offsetY = len > 0 ? (dx / len) * 10 : -10;

    ctx.font = '9px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pathway.harmonic, mx + offsetX, my + offsetY);

    ctx.restore();
  }

  /**
   * Clear pathway: 2px gradient line with glow.
   * Satisfied: higher opacity + glow. Unsatisfied: lower opacity + reduced glow (no dashes).
   */
  private drawClearPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const opacity = satisfied ? 0.6 : 0.25;
    const gradient = ctx.createLinearGradient(sx, sy, tx, ty);
    gradient.addColorStop(0, this.colorWithOpacity(sourceColor, opacity));
    gradient.addColorStop(1, this.colorWithOpacity(targetColor, opacity));

    // Glow effect
    const midColor = this.lerpColor(sourceColor, targetColor, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, satisfied ? 0.5 : 0.15);
    ctx.shadowBlur = satisfied ? 6 : 2;

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Amplifying pathway: 3px thick, two parallel lines, stronger glow.
   */
  private drawAmplifyingPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const opacity = satisfied ? 0.85 : 0.4;
    const gradient = ctx.createLinearGradient(sx, sy, tx, ty);
    gradient.addColorStop(0, this.colorWithOpacity(sourceColor, opacity));
    gradient.addColorStop(1, this.colorWithOpacity(targetColor, opacity));

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    const nx = -dy / len * 1.5;
    const ny = dx / len * 1.5;

    // Strong glow
    const midColor = this.lerpColor(sourceColor, targetColor, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, satisfied ? 0.7 : 0.25);
    ctx.shadowBlur = satisfied ? 10 : 4;

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);

    // Line 1 (offset +1.5px)
    ctx.beginPath();
    ctx.moveTo(sx + nx, sy + ny);
    ctx.lineTo(tx + nx, ty + ny);
    ctx.stroke();

    // Line 2 (offset -1.5px)
    ctx.beginPath();
    ctx.moveTo(sx - nx, sy - ny);
    ctx.lineTo(tx - nx, ty - ny);
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Dampening pathway: 1px thin line, minimal glow, dotted pattern with softer dots.
   */
  private drawDampeningPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const opacity = satisfied ? 0.35 : 0.2;
    const gradient = ctx.createLinearGradient(sx, sy, tx, ty);
    gradient.addColorStop(0, this.colorWithOpacity(sourceColor, opacity));
    gradient.addColorStop(1, this.colorWithOpacity(targetColor, opacity));

    // Minimal glow
    const midColor = this.lerpColor(sourceColor, targetColor, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, 0.15);
    ctx.shadowBlur = 2;

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';
    ctx.setLineDash([2, 6]);

    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.lineCap = 'butt';
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Inverting pathway: sine wave with glow + magenta tint.
   */
  private drawInvertingPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const opacity = satisfied ? 0.75 : 0.35;
    const tintedSource = this.tintMagenta(sourceColor);
    const tintedTarget = this.tintMagenta(targetColor);
    const gradient = ctx.createLinearGradient(sx, sy, tx, ty);
    gradient.addColorStop(0, this.colorWithOpacity(tintedSource, opacity));
    gradient.addColorStop(1, this.colorWithOpacity(tintedTarget, opacity));

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    const nx = -dy / len;
    const ny = dx / len;
    const steps = 40;
    const amplitude = 5;
    const cycles = 6;

    // Glow effect
    const midColor = this.lerpColor(tintedSource, tintedTarget, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, satisfied ? 0.5 : 0.15);
    ctx.shadowBlur = satisfied ? 6 : 2;

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(sx, sy);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const px = sx + dx * t;
      const py = sy + dy * t;
      const wave = Math.sin(t * Math.PI * 2 * cycles) * amplitude;
      ctx.lineTo(px + nx * wave, py + ny * wave);
    }
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Filtering pathway: line with diamond at midpoint + subtle glow on diamond fill.
   */
  private drawFilteringPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const opacity = satisfied ? 0.7 : 0.35;
    const gradient = ctx.createLinearGradient(sx, sy, tx, ty);
    gradient.addColorStop(0, this.colorWithOpacity(sourceColor, opacity));
    gradient.addColorStop(1, this.colorWithOpacity(targetColor, opacity));

    // Glow
    const midColor = this.lerpColor(sourceColor, targetColor, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, satisfied ? 0.4 : 0.12);
    ctx.shadowBlur = satisfied ? 5 : 2;

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);

    // Midpoint
    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;
    const diamondHalf = 5;

    // Direction vector
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    const ux = dx / len;
    const uy = dy / len;
    const dnx = -uy;
    const dny = ux;

    // Diamond corners
    const dTop = { x: mx + ux * diamondHalf, y: my + uy * diamondHalf };
    const dRight = { x: mx + dnx * diamondHalf, y: my + dny * diamondHalf };
    const dBottom = { x: mx - ux * diamondHalf, y: my - uy * diamondHalf };
    const dLeft = { x: mx - dnx * diamondHalf, y: my - dny * diamondHalf };

    // Line from source to diamond
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(dBottom.x, dBottom.y);
    ctx.stroke();

    // Line from diamond to target
    ctx.beginPath();
    ctx.moveTo(dTop.x, dTop.y);
    ctx.lineTo(tx, ty);
    ctx.stroke();

    // Diamond shape with glow fill
    ctx.beginPath();
    ctx.moveTo(dTop.x, dTop.y);
    ctx.lineTo(dRight.x, dRight.y);
    ctx.lineTo(dBottom.x, dBottom.y);
    ctx.lineTo(dLeft.x, dLeft.y);
    ctx.closePath();

    // Glowing fill
    ctx.shadowColor = this.colorWithOpacity(midColor, 0.4);
    ctx.shadowBlur = 6;
    ctx.fillStyle = this.colorWithOpacity(midColor, 0.35);
    ctx.fill();

    ctx.strokeStyle = this.colorWithOpacity(midColor, opacity);
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Resonant pathway: two parallel lines with glow, creating shimmering double-stream.
   */
  private drawResonantPathway(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    const offset = 3;
    const nx = -dy / len * offset;
    const ny = dx / len * offset;

    // Glow effect
    const midColor = this.lerpColor(sourceColor, targetColor, 0.5);
    ctx.shadowColor = this.colorWithOpacity(midColor, satisfied ? 0.5 : 0.15);
    ctx.shadowBlur = satisfied ? 8 : 3;

    ctx.setLineDash([]);

    // Line 1 -- higher opacity
    const gradient1 = ctx.createLinearGradient(sx + nx, sy + ny, tx + nx, ty + ny);
    gradient1.addColorStop(0, this.colorWithOpacity(sourceColor, satisfied ? 0.65 : 0.3));
    gradient1.addColorStop(1, this.colorWithOpacity(targetColor, satisfied ? 0.65 : 0.3));

    ctx.strokeStyle = gradient1;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sx + nx, sy + ny);
    ctx.lineTo(tx + nx, ty + ny);
    ctx.stroke();

    // Line 2 -- slightly lower opacity for shimmer
    const gradient2 = ctx.createLinearGradient(sx - nx, sy - ny, tx - nx, ty - ny);
    gradient2.addColorStop(0, this.colorWithOpacity(sourceColor, satisfied ? 0.45 : 0.2));
    gradient2.addColorStop(1, this.colorWithOpacity(targetColor, satisfied ? 0.45 : 0.2));

    ctx.strokeStyle = gradient2;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sx - nx, sy - ny);
    ctx.lineTo(tx - nx, ty - ny);
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  /**
   * Draws an arrowhead at 80% of the way from source to target.
   */
  private drawArrowhead(
    ctx: CanvasRenderingContext2D,
    sx: number, sy: number, tx: number, ty: number,
    sourceColor: string, targetColor: string,
    satisfied: boolean,
  ): void {
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;

    // Position at 80% along the pathway
    const arrowT = 0.8;
    const ax = sx + dx * arrowT;
    const ay = sy + dy * arrowT;

    // Unit direction vector
    const ux = dx / len;
    const uy = dy / len;

    // Perpendicular
    const px = -uy;
    const py = ux;

    const arrowLen = 8;
    const arrowHalfWidth = 4;

    const tipX = ax + ux * (arrowLen / 2);
    const tipY = ay + uy * (arrowLen / 2);

    const baseX = ax - ux * (arrowLen / 2);
    const baseY = ay - uy * (arrowLen / 2);

    const leftX = baseX + px * arrowHalfWidth;
    const leftY = baseY + py * arrowHalfWidth;
    const rightX = baseX - px * arrowHalfWidth;
    const rightY = baseY - py * arrowHalfWidth;

    const arrowColor = this.lerpColor(sourceColor, targetColor, arrowT);
    const opacity = satisfied ? 0.85 : 0.4;

    ctx.fillStyle = this.colorWithOpacity(arrowColor, opacity);
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(leftX, leftY);
    ctx.lineTo(rightX, rightY);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * Adds a red/magenta tint to a color.
   */
  private tintMagenta(color: string): string {
    const [r, g, b] = this.parseColor(color);

    const nr = Math.min(255, Math.round(r * 0.7 + 255 * 0.3));
    const ng = Math.round(g * 0.6);
    const nb = Math.min(255, Math.round(b * 0.6 + 180 * 0.4));

    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
  }

  private drawNode(node: Node, time?: number, isFocused?: boolean): void {
    const ctx = this.ctx;
    const cx = this.toCanvasX(node.x);
    const cy = this.toCanvasY(node.y);
    const r = this.nodeRadius;

    // Idle pulse animation: subtle breathing on the glow halo
    let pulse = 1;
    if (time !== undefined && !this.reducedMotion) {
      const phase = node.frequency * 0.5;
      pulse = Math.sin((time + phase) * Math.PI) * 0.08 + 1; // 0.92-1.08
    }

    ctx.save();

    const fillColor = this.getNodeColor(node.frequency);
    const glowColor = this.getGlowColor(node.frequency);

    // 1. Outer glow halo (additive blending) — with pulse
    ctx.globalCompositeOperation = 'lighter';
    const haloRadius = r * 2 * pulse;
    const haloGrad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, haloRadius);
    haloGrad.addColorStop(0, this.colorWithOpacity(glowColor, 0.25));
    haloGrad.addColorStop(0.6, this.colorWithOpacity(glowColor, 0.08));
    haloGrad.addColorStop(1, this.colorWithOpacity(glowColor, 0));
    ctx.fillStyle = haloGrad;
    ctx.fillRect(cx - haloRadius, cy - haloRadius, haloRadius * 2, haloRadius * 2);

    // 2. Inner glow ring
    const innerGlowRadius = r * 1.4 * pulse;
    const innerGlow = ctx.createRadialGradient(cx, cy, r * 0.7, cx, cy, innerGlowRadius);
    innerGlow.addColorStop(0, this.colorWithOpacity(glowColor, 0.15));
    innerGlow.addColorStop(1, this.colorWithOpacity(glowColor, 0));
    ctx.fillStyle = innerGlow;
    ctx.fillRect(cx - innerGlowRadius, cy - innerGlowRadius, innerGlowRadius * 2, innerGlowRadius * 2);

    // Switch back to normal blending for core
    ctx.globalCompositeOperation = 'source-over';

    // 3. Core orb - radial gradient for glass/gem look
    const coreGrad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
    coreGrad.addColorStop(0, this.lightenColor(fillColor, 40));
    coreGrad.addColorStop(0.7, fillColor);
    coreGrad.addColorStop(1, this.darkenColor(fillColor, 30));

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = coreGrad;
    ctx.fill();

    // 4. Specular highlight (glass catching light)
    ctx.beginPath();
    ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fill();

    // 5. Border - subtle, in a lighter shade of node color (not white)
    ctx.lineWidth = 1.5;
    if (node.locked) {
      ctx.strokeStyle = this.darkenColor(fillColor, 40);
    } else {
      ctx.strokeStyle = this.colorWithOpacity(this.lightenColor(fillColor, 30), 0.6);
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // If locked, draw dark overlay and crosshatch
    if (node.locked) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fill();
      this.drawCrosshatch(cx, cy, r);
    }

    // 6. Frequency label with text shadow
    if (this.showFrequencyNumbers) {
      const name = FREQUENCY_NAMES[node.frequency] ?? '?';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 3;
      ctx.fillText(name, cx, cy);
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    // 7. Focus ring for keyboard navigation
    if (isFocused) {
      ctx.beginPath();
      ctx.arc(cx, cy, r + 5, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 8. Hint highlight — pulsing golden ring for the first hint node
    if (this._hintHighlightNodeId === node.id) {
      const hintTime = time ?? (performance.now() / 1000);
      const hintPulse = Math.sin(hintTime * 3) * 0.3 + 0.7; // 0.4–1.0
      ctx.beginPath();
      ctx.arc(cx, cy, r + 7, 0, Math.PI * 2);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = `rgba(252, 191, 73, ${hintPulse})`;
      ctx.shadowColor = `rgba(252, 191, 73, ${hintPulse * 0.6})`;
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }

    // 9. Hint sequence badge — numbered indicator at top-right
    const hintIndex = this._hintSequence.indexOf(node.id);
    if (hintIndex >= 0) {
      const badgeX = cx + r * 0.7;
      const badgeY = cy - r * 0.7;
      const badgeR = 8;

      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeR, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(252, 191, 73, 0.9)';
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(180, 130, 30, 0.8)';
      ctx.stroke();

      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = '#1a1a2e';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(hintIndex + 1), badgeX, badgeY);
    }

    ctx.restore();
  }

  /**
   * Converts a hex color string to rgba with the given opacity.
   */
  private colorWithOpacity(color: string, opacity: number): string {
    // Handle rgb() format from lerpColor
    if (color.startsWith('rgb(')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
    }
    // Parse hex color
    const [r, g, b] = this.parseHex(color);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
}
